/**
 * EmailAutomationEditorPage.tsx
 *
 * Dedicated email automation editor at /marketing/automations/email/:id.
 * Split layout: left panel (automation details + contacts), right panel (step flow).
 * Full-screen FtpEditor modal for step content, template picker, step settings modal,
 * timing modal, stats view for active automations, activation/pause/stop controls.
 * Matches the PHP email-automation-details.php behavior exactly.
 */
import { useState, useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Trash2, MoreVertical, Mail, Plus, Pencil, RefreshCw, X, FileText, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PauseAutomationDialog } from '@/components/marketing/PauseAutomationDialog';
import { Spinner } from '@/components/ui/Spinner';
import { AutomationStepMenu } from '@/components/marketing/AutomationStepMenu';
import { useToast } from '@/hooks/useToast';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useStepDragReorder } from '@/hooks/useStepDragReorder';
import { cn } from '@/lib/utils';
import { PERMISSION } from '@/lib/permissions';
import type { BadgeVariant } from '@/types/common.types';
import { apiClient } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import type { AutomationStep } from '@/services/marketing.service';
import { useAuth } from '@/hooks/useAuth';
import { FtpTemplatePicker } from '@/components/ftp-editor/FtpTemplatePicker';
import type { TemplateMetadata, FtpTemplateRow } from '@/components/ftp-editor/FtpTemplatePicker';
import EmailEditorChoiceModal from '@/components/email-editor-host/EmailEditorChoiceModal';
import { detectEditorType, parseDndModel, type EmailEditorType } from '@/components/email-editor-host/editor-type';
import type { EmailEditorHostHandle } from '@/components/email-editor-host/EmailEditorHost';
import { createEmptyModel } from '@/components/dnd-editor-v2';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

const EmailEditorHost = lazyWithRetry(() => import('@/components/email-editor-host/EmailEditorHost'), 'email-editor-host');

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'neutral', active: 'success', paused: 'warning', stopped: 'error',
};

const EXIT_CRITERIA_OPTIONS = [
  { value: '0', label: 'Contacts have received all emails' },
  { value: '1', label: 'Contacts are removed from the list' },
];

const UNSUBSCRIBE_GROUP_OPTIONS = [
  { value: '', label: 'Select group...' },
  { value: 'marketing', label: 'Marketing Emails' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'newsletter', label: 'Newsletter' },
];

const DELAY_UNIT_OPTIONS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

interface Sender {
  emailAccountSenderID: number;
  senderName: string;
  email: string;
  verified: boolean;
}

interface EntryList {
  emailListId: number;
  name: string;
  listType: string;
  patientCount: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getDelayParts(minutes: number): { value: number; unit: string } {
  if (minutes === 0) return { value: 0, unit: 'days' };
  if (minutes < 60) return { value: minutes, unit: 'minutes' };
  if (minutes < 1440) return { value: Math.floor(minutes / 60), unit: 'hours' };
  return { value: Math.floor(minutes / 1440), unit: 'days' };
}

function delayToMinutes(value: number, unit: string): number {
  if (unit === 'hours') return value * 60;
  if (unit === 'days') return value * 1440;
  return value;
}

function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function formatDelay(minutes: number, index: number): string {
  const ord = getOrdinal(index + 1);
  if (minutes === 0) return `Send the ${ord} email instantly`;
  if (minutes < 60) return `Wait ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} then send ${ord} email`;
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60);
    return `Wait ${h} ${h === 1 ? 'hour' : 'hours'} then send ${ord} email`;
  }
  const d = Math.floor(minutes / 1440);
  return `Wait ${d} ${d === 1 ? 'day' : 'days'} then send ${ord} email`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Whether a step has anything in it.
 *
 * Steps no longer arrive with their bodies, so testing `htmlContent` directly
 * would read every unopened step as empty — which would send the editor to the
 * "start from a template" prompt and refuse to activate a perfectly valid
 * automation. Content held locally still wins, because a step edited or created
 * this session is ahead of the flag the server sent with the list.
 */
function stepHasContent(step: AutomationStep): boolean {
  if (step.htmlContent?.trim()) return true;
  if (parseDndModel(step.jsonContent)) return true;
  return step.hasContent === true;
}

/**
 * Thumbnail of one step's email, fetched only once the card is near the viewport.
 *
 * The step list used to render these straight from html the detail response
 * carried for every step. That was the payload that broke the page, so each
 * preview now pulls its own body, cached by step id. `html` short-circuits the
 * fetch for a step whose content the page already holds.
 */
function StepPreview({
  automationId,
  stepId,
  html,
  title,
}: {
  automationId: number;
  stepId: number;
  html?: string;
  title: string;
}) {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView || html !== undefined) return;
    const node = holderRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, html]);

  const { data } = useQuery({
    queryKey: ['marketing', 'automation-step-content', automationId, stepId],
    queryFn: () => marketingSvc.getAutomationStepContent(automationId, stepId),
    // Negative ids are optimistic rows for steps the server has not created yet.
    enabled: inView && html === undefined && stepId > 0,
    staleTime: 5 * 60 * 1000,
  });

  const markup = html ?? data?.htmlContent ?? null;

  return (
    <div ref={holderRef} className="w-full h-full">
      {markup ? (
        <iframe
          srcDoc={markup}
          sandbox=""
          title={title}
          className="w-[600px] h-[466px] border-none origin-top-left scale-[0.3] pointer-events-none"
        />
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 rounded bg-slate-200 animate-pulse" />
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function EmailAutomationEditorPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const automationId = Number(idParam);
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { user: authUser, hasPermission } = useAuth();
  const editorRef = useRef<EmailEditorHostHandle | null>(null);

  // ── Editor access level (parity with the campaign editor) ──
  // Full editing requires permission 21 ("Email Editor Full Access") or level 5;
  // everyone else gets the locked HTML editor (edit only unlocked fields).
  const editorAccessLevel: 'full' | 'locked' =
    ((authUser?.userLevel ?? 0) >= 5 || hasPermission(21)) ? 'full' : 'locked';

  // ── State ──
  const [automationName, setAutomationName] = useState('');
  const [automationDescription, setAutomationDescription] = useState('');
  const [exitCriteria, setExitCriteria] = useState('0');
  const [allowReentry, setAllowReentry] = useState(false);
  const [unsubscribeGroup, setUnsubscribeGroup] = useState('');
  const [categories, setCategories] = useState('');
  const [formInitialized, setFormInitialized] = useState(false);
  const baselineRef = useRef<Record<string, unknown>>({});

  const [stepEditorUnsaved, setStepEditorUnsaved] = useState(false);
  const [suppressUnsaved, setSuppressUnsaved] = useState(true);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState(-1);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsStepIndex, setSettingsStepIndex] = useState(-1);
  const [showTimingModal, setShowTimingModal] = useState(false);
  const [timingStepIndex, setTimingStepIndex] = useState(-1);
  const [timingValue, setTimingValue] = useState(0);
  const [timingUnit, setTimingUnit] = useState('days');
  const [settingsSubject, setSettingsSubject] = useState('');
  const [settingsPreheader, setSettingsPreheader] = useState('');
  const [settingsSender, setSettingsSender] = useState('');
  const [settingsSenderName, setSettingsSenderName] = useState('');
  const [settingsDelayValue, setSettingsDelayValue] = useState(0);
  const [settingsDelayUnit, setSettingsDelayUnit] = useState('days');
  const [showTemplateChoice, setShowTemplateChoice] = useState(false);
  const [showEditorChoice, setShowEditorChoice] = useState(false);
  const [editorType, setEditorType] = useState<EmailEditorType>('html');
  const [editorSeed, setEditorSeed] = useState(0);
  const [editorInit, setEditorInit] = useState<{ html: string; plain: string; json: string | null }>({ html: '', plain: '', json: null });
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templatePickerMode, setTemplatePickerMode] = useState<'add_step' | 'empty_step' | 'editor'>('add_step');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [loadingOverlay, setLoadingOverlay] = useState('');
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [listTypeFilter, setListTypeFilter] = useState<'static' | 'dynamic'>('static');
  const [selectedEntryListId, setSelectedEntryListId] = useState<string>('');

  // Local step state for optimistic updates
  const [localSteps, setLocalSteps] = useState<AutomationStep[]>([]);

  // ── Queries ──
  const { data: automation, isLoading } = useQuery({
    queryKey: ['automation-editor', automationId],
    queryFn: () => marketingSvc.getAutomation(automationId),
    enabled: automationId > 0,
  });

  const { data: senders = [] } = useQuery({
    queryKey: ['marketing-senders'],
    queryFn: async () => {
      const { data } = await apiClient.get('/marketing/settings/senders');
      return (Array.isArray(data) ? data : data?.data ?? []) as Sender[];
    },
  });

  const { data: entryLists = [] } = useQuery({
    queryKey: ['marketing-entry-lists', 'email'],
    queryFn: () => marketingSvc.listEntryLists('email'),
  });


  // Sync local steps from automation data
  useEffect(() => {
    if (automation?.steps) {
      setLocalSteps(automation.steps);
    }
  }, [automation?.steps]);

  // Sync entry list selection from automation data
  useEffect(() => {
    if (automation?.entryListId && !selectedEntryListId) {
      setSelectedEntryListId(automation.entryListId.toString());
      const matchingList = (entryLists as EntryList[]).find(l => l.emailListId === automation.entryListId);
      if (matchingList) {
        setListTypeFilter((matchingList.listType || 'static') as 'static' | 'dynamic');
      }
    }
  }, [automation?.entryListId, entryLists, selectedEntryListId]);

  // Initialize form fields from automation data
  useEffect(() => {
    if (automation && !formInitialized) {
      const resolvedName = automation.name ?? '';
      const resolvedDesc = automation.description ?? '';
      const resolvedExit = automation.exitCriteria?.toString() ?? '0';
      const resolvedReentry = !!automation.allowReentry;
      const resolvedUnsub = automation.unsubscribeGroup ?? '';
      const resolvedCats = automation.categories ?? '';
      const resolvedEntryList = automation.entryListId?.toString() ?? '';

      setAutomationName(resolvedName);
      setAutomationDescription(resolvedDesc);
      setExitCriteria(resolvedExit);
      setAllowReentry(resolvedReentry);
      setUnsubscribeGroup(resolvedUnsub);
      setCategories(resolvedCats);
      if (!selectedEntryListId) setSelectedEntryListId(resolvedEntryList);

      baselineRef.current = {
        name: resolvedName, description: resolvedDesc, exitCriteria: resolvedExit,
        allowReentry: resolvedReentry, unsubscribeGroup: resolvedUnsub,
        categories: resolvedCats, entryListId: resolvedEntryList,
      };
      setFormInitialized(true);
    }
  }, [automation, formInitialized, selectedEntryListId]);

  const hasUnsavedChanges = useMemo(() => {
    if (!automation || !formInitialized) return false;
    const b = baselineRef.current;
    if (automationName !== b.name) return true;
    if (automationDescription !== b.description) return true;
    if (exitCriteria !== b.exitCriteria) return true;
    if (allowReentry !== b.allowReentry) return true;
    if (unsubscribeGroup !== b.unsubscribeGroup) return true;
    if (categories !== b.categories) return true;
    if (selectedEntryListId !== b.entryListId) return true;
    return false;
  }, [automation, formInitialized, automationName, automationDescription, exitCriteria, allowReentry, unsubscribeGroup, categories, selectedEntryListId]);

  // Lift suppression after init
  useEffect(() => {
    const timer = setTimeout(() => setSuppressUnsaved(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Redirect active automations to stats page (stopped are editable)
  useEffect(() => {
    if (automation?.status === 'active') {
      navigate(`/marketing/automations/email/${automationId}/stats`, { replace: true });
    }
  }, [automation?.status, automationId, navigate]);

  // Navigation guards
  useUnsavedChangesGuard(hasUnsavedChanges || stepEditorUnsaved);

  // ── Mutations ──
  const saveMut = useMutation({
    mutationFn: (data: Parameters<typeof marketingSvc.updateAutomation>[1]) =>
      marketingSvc.updateAutomation(automationId, data),
    onSuccess: () => {
      baselineRef.current = {
        name: automationName, description: automationDescription, exitCriteria,
        allowReentry, unsubscribeGroup, categories, entryListId: selectedEntryListId,
      };
      qc.invalidateQueries({ queryKey: ['automation-editor', automationId] });
      toast.success('Automation saved');
    },
    onError: () => toast.error('Failed to save'),
  });

  const addStepMut = useMutation({
    mutationFn: (data: Partial<AutomationStep>) => marketingSvc.addAutomationStep(automationId, data),
    onSuccess: (newStep) => {
      setLocalSteps(prev => [...prev.filter(s => !(s as any).loading), newStep]);
      qc.invalidateQueries({ queryKey: ['automation-editor', automationId] });
      toast.success('Step added');
    },
    onError: () => {
      setLocalSteps(prev => prev.filter(s => !(s as any).loading));
      toast.error('Failed to add step');
    },
  });

  const updateStepMut = useMutation({
    mutationFn: ({ stepId, data }: { stepId: number; data: Partial<AutomationStep> }) =>
      marketingSvc.updateAutomationStep(automationId, stepId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-editor', automationId] });
    },
  });

  const deleteStepMut = useMutation({
    mutationFn: (stepId: number) => marketingSvc.deleteAutomationStep(automationId, stepId),
    onSuccess: (_, stepId) => {
      setLocalSteps(prev => prev.filter(s => s.stepId !== stepId));
      qc.invalidateQueries({ queryKey: ['automation-editor', automationId] });
      toast.success('Step deleted');
    },
    onError: () => toast.error('Failed to delete step'),
  });

  // ── Step reordering ──
  // The order the list held before the drag, so a failed reorder can be put
  // back. The cards move optimistically: a step list is short and the server
  // rewrites the whole sequence, so an instant move with a hard revert reads
  // better than cards frozen mid-drag while a request is in flight.
  const reorderRevertRef = useRef<AutomationStep[] | null>(null);

  const reorderMut = useMutation({
    mutationFn: (stepIds: number[]) => marketingSvc.reorderAutomationSteps(automationId, stepIds),
    onSuccess: ({ contactsRemapped }) => {
      reorderRevertRef.current = null;
      qc.invalidateQueries({ queryKey: ['automation-editor', automationId] });
      // Say so when live contacts moved with the steps — the person reordering a
      // paused automation has no other way to know that happened.
      toast.success(
        contactsRemapped > 0
          ? `Steps reordered — ${contactsRemapped.toLocaleString()} in-progress contact${contactsRemapped === 1 ? '' : 's'} realigned`
          : 'Steps reordered',
      );
    },
    onError: (err: any) => {
      if (reorderRevertRef.current) setLocalSteps(reorderRevertRef.current);
      reorderRevertRef.current = null;
      const serverMsg = err?.response?.data?.error?.message;
      toast.error(serverMsg || 'Failed to reorder steps');
    },
  });

  const applyReorder = useCallback((next: AutomationStep[], previous: AutomationStep[]) => {
    reorderRevertRef.current = previous;
    setLocalSteps(next);
    reorderMut.mutate(next.map(s => s.stepId));
  }, [reorderMut]);

  const stepDrag = useStepDragReorder<AutomationStep>({
    items: localSteps,
    onReorder: applyReorder,
    // A step still being created has a temporary negative id the server would
    // reject, and a reorder mid-flight would race the create.
    disabled: localSteps.some(s => (s as any).loading) || reorderMut.isPending,
  });

  /** Keyboard/menu equivalent of a drag, so reordering is not mouse-only. */
  const moveStep = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= localSteps.length) return;
    const next = [...localSteps];
    [next[index], next[target]] = [next[target], next[index]];
    applyReorder(next, localSteps);
  }, [localSteps, applyReorder]);

  const activateMut = useMutation({
    mutationFn: () => marketingSvc.activateAutomation(automationId),
    onSuccess: () => {
      setLoadingOverlay('');
      qc.invalidateQueries({ queryKey: ['automation-editor', automationId] });
      toast.success('Automation activated');
      navigate(`/marketing/automations/email/${automationId}/stats`);
    },
    onError: (err: any) => {
      setLoadingOverlay('');
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Failed to activate';
      const debugInfo = err?.response?.data?.file ? ` (${err.response.data.file}:${err.response.data.line})` : '';
      toast.error(serverMsg + debugInfo);
      console.error('Activate error:', err?.response?.data || err);
    },
  });

  const pauseMut = useMutation({
    mutationFn: () => marketingSvc.pauseAutomation(automationId),
    onSuccess: () => {
      setLoadingOverlay('');
      qc.invalidateQueries({ queryKey: ['automation-editor', automationId] });
      toast.success('Automation paused');
    },
    onError: () => { setLoadingOverlay(''); toast.error('Failed to pause'); },
  });

  const deleteMut = useMutation({
    mutationFn: () => marketingSvc.deleteAutomation(automationId),
    onSuccess: () => {
      navigate('/marketing/automations');
      toast.success('Automation deleted');
    },
    onError: () => toast.error('Cannot delete automation with active enrollments'),
  });

  const syncMut = useMutation({
    mutationFn: () => marketingSvc.syncAutomationContacts(automationId),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['automation-editor', automationId] });
      toast.success(`Sync complete: ${result.added} added, ${result.removed} removed, ${result.skipped} skipped`);
    },
    onError: () => toast.error('Sync failed'),
  });

  // ── Computed ──
  const isDraft = automation?.status === 'draft';
  const isActive = automation?.status === 'active';
  const isPaused = automation?.status === 'paused';
  const contacts = automation?.contactCounts ?? { active: 0, completed: 0, removed: 0, unsubscribed: 0, total: 0 };

  const filteredLists = useMemo(() =>
    (entryLists as EntryList[]).filter(l => (l.listType || 'static') === listTypeFilter),
    [entryLists, listTypeFilter]
  );

  const activationMissing = useMemo(() => {
    if (!automation) return ['Loading...'];
    const missing: string[] = [];
    if (hasUnsavedChanges || stepEditorUnsaved) missing.push('Save all changes first');
    if (!automation.name?.trim()) missing.push('Automation name');
    if (!automation.entryListId) missing.push('Entry list');
    let hasValid = false;
    let hasIncomplete = false;
    for (const step of localSteps) {
      if ((step as any).loading) continue;
      const hasHtml = stepHasContent(step);
      const hasSubj = step.subject?.trim();
      const hasEmail = step.senderEmail?.trim();
      const hasName = step.senderName?.trim();
      if (hasHtml && hasSubj && hasEmail && hasName) hasValid = true;
      else hasIncomplete = true;
    }
    if (!hasValid) missing.push('At least one complete step (subject, from email, from name, and HTML content)');
    if (hasIncomplete) missing.push('All steps must have subject, from email, from name, and HTML content');
    return missing;
  }, [automation, localSteps, hasUnsavedChanges, stepEditorUnsaved]);

  const canActivate = activationMissing.length === 0;

  // ── Handlers ──
  function markStepEditorUnsaved() {
    if (suppressUnsaved) return;
    setStepEditorUnsaved(true);
  }

  function handleSave() {
    if (!automation) return;
    setLoadingOverlay('Saving...');
    saveMut.mutate({
      name: automationName || automation.name,
      description: automationDescription || undefined,
      entryListId: isDraft ? (Number(selectedEntryListId) || automation.entryListId) : automation.entryListId,
      exitCriteria: Number(exitCriteria) || automation.exitCriteria,
      allowReentry,
      unsubscribeGroup: unsubscribeGroup || undefined,
      categories: categories || undefined,
    }, {
      onSettled: () => setLoadingOverlay(''),
    });
  }

  const handleBack = useCallback(() => {
    navigate('/marketing/automations');
  }, [navigate]);

  function handleActivate() {
    if (hasUnsavedChanges || stepEditorUnsaved) {
      toast.error('Please save all changes before activating.');
      return;
    }
    if (!canActivate) {
      toast.error('Missing: ' + activationMissing.join(', '));
      return;
    }
    setLoadingOverlay(isPaused ? 'Resuming automation...' : 'Activating automation...');
    activateMut.mutate();
  }

  // The confirm dialog owns the button spinner; errors surface via pauseMut.onError.
  async function confirmPause() {
    try {
      await pauseMut.mutateAsync();
    } catch {
      /* surfaced by pauseMut.onError */
    }
  }

  function handleAddStep() {
    setTemplatePickerMode('add_step');
    setShowTemplateChoice(true);
  }

  function handleTemplateStartFromScratch() {
    // Starting blank → let the user pick the drag-and-drop or HTML editor.
    setShowTemplateChoice(false);
    setShowTemplatePicker(false);
    setShowEditorChoice(true);
  }

  function handleEditorChoice(type: EmailEditorType) {
    setShowEditorChoice(false);
    const defaultHtml = type === 'dnd'
      ? ''
      : '<!DOCTYPE html>\n<html>\n<head><meta charset="utf-8"></head>\n<body>\n</body>\n</html>';
    const defaultJson = type === 'dnd' ? JSON.stringify(createEmptyModel()) : null;
    if (templatePickerMode === 'empty_step' && editingStepIndex >= 0) {
      const step = localSteps[editingStepIndex];
      if (step) {
        updateStepMut.mutate({ stepId: step.stepId, data: { htmlContent: defaultHtml, jsonContent: defaultJson } });
        setLocalSteps(prev => prev.map((s, i) => i === editingStepIndex ? { ...s, htmlContent: defaultHtml, jsonContent: defaultJson } : s));
        openStepEditor(editingStepIndex, type, defaultHtml, '', defaultJson);
      }
    } else {
      createStepWithContent(defaultHtml, '', '', '', '', '', defaultJson ?? undefined);
    }
  }

  function handleChooseTemplate() {
    setShowTemplateChoice(false);
    setShowTemplatePicker(true);
  }

  function handleTemplateApply(htmlContent: string, plainContent?: string, jsonContent?: string, metadata?: TemplateMetadata) {
    setShowTemplatePicker(false);
    if (templatePickerMode === 'add_step') {
      createStepWithContent(
        htmlContent,
        plainContent || '',
        metadata?.subject || '',
        metadata?.preheader || '',
        metadata?.fromEmail || '',
        metadata?.fromName || '',
        jsonContent,
      );
    } else if (templatePickerMode === 'empty_step' && editingStepIndex >= 0) {
      const step = localSteps[editingStepIndex];
      if (step) {
        const updData: Partial<AutomationStep> = { htmlContent, jsonContent: jsonContent ?? null };
        if (plainContent) updData.plainContent = plainContent;
        if (metadata?.subject) updData.subject = metadata.subject;
        if (metadata?.preheader) updData.preheader = metadata.preheader;
        if (metadata?.fromEmail) updData.senderEmail = metadata.fromEmail;
        if (metadata?.fromName) updData.senderName = metadata.fromName;
        updateStepMut.mutate({ stepId: step.stepId, data: updData });
        setLocalSteps(prev => prev.map((s, i) => i === editingStepIndex ? { ...s, ...updData } : s));
        openStepEditor(editingStepIndex, detectEditorType(jsonContent ?? null), htmlContent, plainContent || '', jsonContent ?? null);
      }
    } else if (templatePickerMode === 'editor') {
      // Swap the open editor to the template's content (and editor type).
      setEditorType(detectEditorType(jsonContent ?? null));
      setEditorInit({ html: htmlContent, plain: plainContent || '', json: jsonContent ?? null });
      setEditorSeed(s => s + 1);
      markStepEditorUnsaved();
    }
  }

  function createStepWithContent(html: string, plain: string, subject: string, preheader: string, senderEmail: string, senderName: string, jsonContent?: string) {
    const defaultDelay = localSteps.length > 0 ? 1440 : 0;
    const tempStep: AutomationStep & { loading?: boolean } = {
      stepId: -Date.now(),
      stepOrder: localSteps.length + 1,
      delayMinutes: defaultDelay,
      htmlContent: html,
      plainContent: plain,
      jsonContent: jsonContent ?? null,
      subject,
      preheader,
      senderEmail,
      senderName,
      loading: true,
    } as any;
    setLocalSteps(prev => [...prev, tempStep]);
    // No stepOrder: the server appends. It now honours stepOrder as an
    // insert-AT position, so sending a count-derived one would mid-insert the
    // step if the local list were ever a row behind the table.
    addStepMut.mutate({
      delayMinutes: defaultDelay,
      htmlContent: html,
      plainContent: plain,
      jsonContent: jsonContent ?? null,
      subject,
      preheader,
      senderEmail,
      senderName,
    });
  }

  // Mount the step editor seeded with the given content + editor type (seeded at
  // mount rather than injected, so DnD steps load into the DnD editor).
  function openStepEditor(index: number, type: EmailEditorType, html: string, plain: string, json: string | null) {
    setEditingStepIndex(index);
    setEditorType(type);
    setEditorInit({ html, plain, json });
    setEditorSeed(s => s + 1);
    setStepEditorUnsaved(false);
    setSuppressUnsaved(true);
    setShowStepEditor(true);
    setTimeout(() => setSuppressUnsaved(false), 700);
  }

  async function openStepEditorForIndex(index: number) {
    const step = localSteps[index];
    if (!step) return;
    if (!stepHasContent(step)) {
      setTemplatePickerMode('empty_step');
      setEditingStepIndex(index);
      setShowTemplateChoice(true);
      return;
    }

    // Steps arrive without their bodies, so fetch this one's before seeding the
    // editor. A step whose content is already in hand — just edited, or created
    // from a template this session — is used as-is rather than re-fetched.
    let html = step.htmlContent;
    let plain = step.plainContent;
    let json = step.jsonContent ?? null;

    if (html === undefined) {
      setLoadingOverlay('Loading step...');
      try {
        const content = await marketingSvc.getAutomationStepContent(automationId, step.stepId);
        html = content.htmlContent ?? '';
        plain = content.plainContent ?? '';
        json = content.jsonContent ?? null;
        setLocalSteps(prev =>
          prev.map((s, i) =>
            i === index ? { ...s, htmlContent: html, plainContent: plain, jsonContent: json } : s,
          ),
        );
      } catch {
        toast.error("Could not load this step's content");
        return;
      } finally {
        setLoadingOverlay('');
      }
    }

    // Routed on the real jsonContent, not the server's isDnd hint.
    openStepEditor(index, detectEditorType(json), html || '', plain || '', json);
  }

  function handleSaveStepContent() {
    if (editingStepIndex < 0) return;
    const step = localSteps[editingStepIndex];
    if (!step) return;

    if (!editorRef.current) return;

    editorRef.current.updateHiddenField();
    const html = editorRef.current.getContent();
    const plain = editorRef.current.getPlainContent();

    const updData: Partial<import('@/services/marketing.service').AutomationStep> = {
      htmlContent: html,
      plainContent: plain,
      jsonContent: editorRef.current.getJson(),
    };
    setLocalSteps(prev => prev.map((s, i) => i === editingStepIndex ? { ...s, ...updData } : s));
    updateStepMut.mutate({ stepId: step.stepId, data: updData });
    setStepEditorUnsaved(false);
    setShowStepEditor(false);
    toast.success('Email content saved');
  }

  function handleCloseStepEditor() {
    if (stepEditorUnsaved && !confirm('You have unsaved changes to this email. Close without saving?')) return;
    setStepEditorUnsaved(false);
    setShowStepEditor(false);
    setEditingStepIndex(-1);
  }

  function openSettingsModal(index: number) {
    const step = localSteps[index];
    if (!step) return;
    setSettingsStepIndex(index);
    const dp = getDelayParts(step.delayMinutes);
    setSettingsDelayValue(dp.value);
    setSettingsDelayUnit(dp.unit);
    setSettingsSubject(step.subject || '');
    setSettingsPreheader(step.preheader || '');
    setSettingsSender(step.senderEmail || '');
    setSettingsSenderName(step.senderName || '');
    setShowSettingsModal(true);
  }

  function handleSaveSettings() {
    if (settingsStepIndex < 0) return;
    const step = localSteps[settingsStepIndex];
    if (!step) return;
    const dm = delayToMinutes(settingsDelayValue, settingsDelayUnit);
    const updData: Partial<AutomationStep> = {
      delayMinutes: dm,
      subject: settingsSubject,
      preheader: settingsPreheader,
      senderEmail: settingsSender,
      senderName: settingsSenderName,
    };
    setLocalSteps(prev => prev.map((s, i) => i === settingsStepIndex ? { ...s, ...updData } : s));
    updateStepMut.mutate({ stepId: step.stepId, data: updData });
    setShowSettingsModal(false);
    toast.success('Settings saved');
  }

  function openTimingModal(index: number) {
    const step = localSteps[index];
    if (!step) return;
    setTimingStepIndex(index);
    const dp = getDelayParts(step.delayMinutes);
    setTimingValue(dp.value);
    setTimingUnit(dp.unit);
    setShowTimingModal(true);
  }

  function handleSaveTiming() {
    if (timingStepIndex < 0) return;
    const step = localSteps[timingStepIndex];
    if (!step) return;
    const dm = delayToMinutes(timingValue, timingUnit);
    setLocalSteps(prev => prev.map((s, i) => i === timingStepIndex ? { ...s, delayMinutes: dm } : s));
    updateStepMut.mutate({ stepId: step.stepId, data: { delayMinutes: dm } });
    setShowTimingModal(false);
    toast.success('Timing saved');
  }

  function handleDeleteStep(index: number) {
    const step = localSteps[index];
    if (!step || !confirm('Are you sure you want to delete this email step?')) return;
    deleteStepMut.mutate(step.stepId);
  }

  // ── Keyboard handlers ──
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (showStepEditor) handleCloseStepEditor();
        else if (showTemplatePicker) setShowTemplatePicker(false);
        else if (showTemplateChoice) setShowTemplateChoice(false);
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  // ── Loading state ──
  if (isLoading || !automation) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }


  // ── Editor View ──
  return (
    <div className="flex flex-col h-full">
      {/* Loading Overlay */}
      {loadingOverlay && (
        <div className="fixed inset-0 bg-white/70 z-[99999] flex flex-col items-center justify-center gap-4">
          <Spinner size="lg" />
          <span className="text-sm font-semibold text-slate-600">{loadingOverlay}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0 relative z-40">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold text-slate-900">{automation.name || 'Untitled Automation'}</h1>
          <Badge variant={STATUS_BADGE[automation.status] ?? 'neutral'} dot>
            {capitalize(automation.status)}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {(hasUnsavedChanges || stepEditorUnsaved) && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-300 rounded text-amber-700 text-xs font-medium">
              <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
              Unsaved changes
            </div>
          )}
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" leftIcon={<Save className="h-4 w-4" />} onClick={handleSave}>
            Save Changes
          </Button>
          {isDraft && (
            <Button
              size="sm"
              disabled={!canActivate}
              title={canActivate ? '' : 'Missing: ' + activationMissing.join(', ')}
              onClick={handleActivate}
            >
              Activate
            </Button>
          )}
          {isActive && (
            <Button variant="secondary" size="sm" onClick={() => setPauseConfirmOpen(true)}>Pause</Button>
          )}
          {isPaused && (
            <>
              <Button variant="secondary" size="sm" onClick={() => navigate(`/marketing/automations/email/${automationId}/stats`)}>View Stats</Button>
              <Button size="sm" onClick={handleActivate}>Resume</Button>
            </>
          )}
          {isDraft && (
            <div className="relative">
              <Button variant="ghost" size="sm" onClick={() => setShowMoreMenu(!showMoreMenu)}>
                <MoreVertical className="h-4 w-4" />
              </Button>
              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[160px]">
                    <button
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      onClick={() => { setShowMoreMenu(false); setShowDeleteConfirm(true); }}
                    >
                      <Trash2 className="inline h-4 w-4 mr-2" />Delete Automation
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Split Panel */}
      <div className="flex flex-1 min-h-0">
        {/* Left Panel: Details */}
        <div className="w-[42%] min-w-[380px] max-w-[520px] border-r border-slate-200 overflow-y-auto">
          <form id="automationForm" className="p-5 space-y-5">
            <Card>
              <CardHeader><span className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-blue-600" /> Email Automation Details</span></CardHeader>
              <CardBody className="space-y-4">
                <FormField label="Automation Name" required>
                  <Input name="name" value={automationName} onChange={(e) => setAutomationName(e.target.value)} placeholder="e.g., Welcome Series" />
                </FormField>
                <FormField label="Description" helpText="For internal reference only">
                  <Textarea autoResize name="description" value={automationDescription} onChange={(e) => setAutomationDescription(e.target.value)} placeholder="Internal notes..." rows={2} />
                </FormField>

                {/* Entry List */}
                <FormField
                  label="Entry List"
                  helpText="Automation triggers when a contact is added to this list"
                  labelExtra={isDraft ? (
                    <span className="ml-auto flex items-center gap-1">
                      <button type="button" className={cn('px-2 py-1 text-xs font-semibold border rounded', listTypeFilter === 'static' ? 'bg-blue-50 border-blue-600 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-500')} onClick={() => setListTypeFilter('static')}>Static</button>
                      <button type="button" className={cn('px-2 py-1 text-xs font-semibold border rounded', listTypeFilter === 'dynamic' ? 'bg-orange-50 border-orange-600 text-orange-700' : 'bg-slate-50 border-slate-200 text-slate-500')} onClick={() => setListTypeFilter('dynamic')}>Dynamic</button>
                    </span>
                  ) : undefined}
                >
                  {isDraft ? (
                    <Select
                      name="entry_list_id"
                      value={selectedEntryListId}
                      onChange={v => setSelectedEntryListId(v || '')}
                      options={[
                        { value: '', label: 'Select a list...' },
                        ...filteredLists.map(l => ({ value: l.emailListId.toString(), label: `${l.name} (${capitalize(l.listType || 'static')}) — ${l.patientCount.toLocaleString()} emails` })),
                      ]}
                    />
                  ) : (
                    <Input value={automation.entryListName || 'No list selected'} disabled className="bg-slate-50" />
                  )}
                  {!isDraft && <input type="hidden" name="entry_list_id" value={automation.entryListId || ''} />}
                </FormField>

                <FormField label="Exit Criteria" helpText="When contacts will exit and stop receiving emails">
                  <Select name="exit_criteria" value={exitCriteria} options={EXIT_CRITERIA_OPTIONS} onChange={v => setExitCriteria(v || '0')} />
                </FormField>

                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" name="allow_reentry" value="1" checked={allowReentry} onChange={(e) => setAllowReentry(e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm font-semibold">Allow Re-entry</span>
                </label>
                <p className="text-xs text-slate-500 ml-7 -mt-2">Contacts who leave and return will restart the automation</p>

                <FormField label="Unsubscribe Group">
                  <Select name="unsubscribe_group" value={unsubscribeGroup} options={UNSUBSCRIBE_GROUP_OPTIONS} onChange={v => setUnsubscribeGroup(v || '')} />
                </FormField>
                <FormField label="Categories">
                  <Input name="categories" value={categories} onChange={(e) => setCategories(e.target.value)} placeholder="e.g., welcome, onboarding" />
                </FormField>
              </CardBody>
            </Card>

            {/* Contacts Section */}
            {contacts.total > 0 && (
              <Card>
                <CardHeader><span className="font-semibold">Contacts</span></CardHeader>
                <CardBody>
                  <div className="flex gap-5 flex-wrap text-sm">
                    <div><strong>{contacts.active.toLocaleString()}</strong> <span className="text-slate-500">Active</span></div>
                    <div><strong>{contacts.completed.toLocaleString()}</strong> <span className="text-slate-500">Completed</span></div>
                    <div><strong>{contacts.removed.toLocaleString()}</strong> <span className="text-slate-500">Removed</span></div>
                    <div><strong>{contacts.unsubscribed.toLocaleString()}</strong> <span className="text-slate-500">Unsubscribed</span></div>
                    <div><strong>{contacts.total.toLocaleString()}</strong> <span className="text-slate-500">Total</span></div>
                  </div>
                  {isActive && (
                    <Button variant="secondary" size="sm" className="mt-3" onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
                      <RefreshCw className={cn('h-3.5 w-3.5 mr-1', syncMut.isPending && 'animate-spin')} />
                      Sync Contacts Now
                    </Button>
                  )}
                </CardBody>
              </Card>
            )}
          </form>
        </div>

        {/* Right Panel: Steps */}
        <div className="flex-1 min-w-[400px] overflow-y-auto bg-slate-50">
          <div className="px-6 pt-4 pb-2 text-lg font-bold text-slate-800">Email Steps</div>
          <div className="px-6 pb-6">
            {localSteps.length === 0 ? (
              <>
                <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded cursor-pointer hover:border-blue-500 transition-colors" onClick={() => handleAddStep()}>
                  <span className="flex-1 text-sm text-slate-700">Send the 1st email instantly</span>
                  <Pencil className="h-4 w-4 text-slate-400" />
                </div>
                <div className="w-0.5 h-6 bg-slate-200 ml-5" />
                <div className="bg-slate-50 border border-slate-200 rounded overflow-hidden cursor-pointer hover:border-blue-500 transition-colors" onClick={() => handleAddStep()}>
                  <div className="flex items-stretch">
                    <div className="writing-mode-vertical bg-slate-200 px-1.5 py-3 text-[11px] font-semibold text-slate-500 flex items-center justify-center" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>EMAIL 1</div>
                    <div className="flex-1 flex items-center justify-center p-8 text-slate-400">
                      <div className="text-center"><Mail className="h-8 w-8 mx-auto mb-2 opacity-50" /><span className="text-xs font-medium">Click to add email</span></div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              localSteps.map((step, index) => {
                const isLoadingStep = (step as any).loading;
                const hasContent = stepHasContent(step);
                // A single step has nowhere to go, and a step mid-create has a
                // temporary id the reorder endpoint would reject.
                const canReorder = localSteps.length > 1 && !isLoadingStep && !reorderMut.isPending;
                const isDragged = stepDrag.dragIndex === index;
                return (
                  <div
                    key={step.stepId}
                    data-step-card
                    // Drop targeting lives on the whole step — its delay block
                    // included — because the delay belongs to the step and moves
                    // with it.
                    onDragEnter={stepDrag.handleDragEnter}
                    onDragLeave={stepDrag.handleDragLeave}
                    onDragOver={(e) => stepDrag.handleDragOver(e, index)}
                    onDrop={stepDrag.handleDrop}
                    className={cn('relative', isDragged && 'opacity-40')}
                  >
                    {stepDrag.dropIndex === index && (
                      <div className="absolute -top-1 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
                    )}
                    {/* Timing block */}
                    <div
                      className={cn('flex items-center gap-3 p-3 bg-white border border-slate-200 rounded transition-colors', !isLoadingStep && 'cursor-pointer hover:border-blue-500')}
                      onClick={() => !isLoadingStep && openTimingModal(index)}
                    >
                      <span className="flex-1 text-sm text-slate-700">
                        {isLoadingStep ? <><Spinner size="sm" className="inline mr-2" />Loading...</> : formatDelay(step.delayMinutes, index)}
                      </span>
                      {!isLoadingStep && <Pencil className="h-4 w-4 text-slate-400" />}
                    </div>
                    <div className="w-0.5 h-6 bg-slate-200 ml-5" />
                    {/* Email block */}
                    <div className={cn('bg-white border border-slate-200 rounded overflow-hidden', isLoadingStep && 'opacity-70 pointer-events-none')}>
                      <div className="flex items-stretch">
                        {/* The step's own label doubles as the drag handle, so the
                            card keeps its click targets (open editor, open menu).
                            draggable sits here only — matching the DnD editor's
                            grip-handle pattern. */}
                        <div
                          className={cn(
                            'bg-slate-200 px-1.5 py-3 text-[11px] font-semibold text-slate-500 flex flex-col items-center justify-center gap-1.5 select-none',
                            canReorder && 'cursor-grab active:cursor-grabbing hover:bg-slate-300 transition-colors',
                          )}
                          draggable={canReorder}
                          onDragStart={(e) => stepDrag.handleDragStart(
                            e,
                            index,
                            (e.currentTarget as HTMLElement).closest('[data-step-card]') as HTMLElement | null,
                          )}
                          onDragEnd={stepDrag.handleDragEnd}
                          title={canReorder ? 'Drag to reorder this email' : undefined}
                        >
                          {canReorder && <GripVertical className="h-3.5 w-3.5 shrink-0" />}
                          <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>EMAIL {index + 1}</span>
                        </div>
                        {/* Preview */}
                        <div
                          className="w-[180px] h-[140px] bg-slate-50 border-r border-slate-200 overflow-hidden relative cursor-pointer shrink-0 group"
                          onClick={() => !isLoadingStep && openStepEditorForIndex(index)}
                        >
                          {isLoadingStep ? (
                            <div className="flex items-center justify-center h-full"><Spinner size="sm" /></div>
                          ) : hasContent ? (
                            <>
                              <StepPreview
                                automationId={automationId}
                                stepId={step.stepId}
                                html={step.htmlContent}
                                title={`Email ${index + 1}`}
                              />
                              <div className="absolute inset-0 bg-blue-600/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <span className="text-white text-xs font-semibold">Edit Content</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                <Mail className="h-8 w-8 mb-1 opacity-50" />
                                <span className="text-[11px] font-medium">Add Email Content</span>
                              </div>
                              <div className="absolute inset-0 bg-blue-600/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <span className="text-white text-xs font-semibold">Create Email</span>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Details */}
                        <div className="flex-1 p-4 flex flex-col justify-center text-sm space-y-1.5">
                          <div><strong className="text-slate-700">Subject</strong> <span className="text-red-500">*</span> {step.subject ? <span className="text-blue-600">{step.subject}</span> : <span className="text-red-500 italic">Add subject</span>}</div>
                          <div><strong className="text-slate-700">Preheader</strong> {step.preheader ? <span className="text-blue-600">{step.preheader}</span> : <span className="text-slate-400">Not set</span>}</div>
                          <div><strong className="text-slate-700">From</strong> <span className="text-red-500">*</span> {step.senderName && step.senderEmail ? <span className="text-blue-600">{step.senderName} &lt;{step.senderEmail}&gt;</span> : <span className="text-red-500 italic">Select Sender</span>}</div>
                        </div>
                        {/* Menu */}
                        {!isLoadingStep && (
                          <div className="p-4">
                            <AutomationStepMenu
                              stepNumber={index + 1}
                              canMoveUp={index > 0 && canReorder}
                              canMoveDown={index < localSteps.length - 1 && canReorder}
                              onEdit={() => openSettingsModal(index)}
                              onMoveUp={() => moveStep(index, -1)}
                              onMoveDown={() => moveStep(index, 1)}
                              onDelete={() => handleDeleteStep(index)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {index < localSteps.length - 1 && <div className="w-0.5 h-6 bg-slate-200 ml-5" />}
                    {/* Only the last card renders the trailing slot; every other
                        one is covered by the next card's leading indicator. */}
                    {stepDrag.dropIndex === index + 1 && index === localSteps.length - 1 && (
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
                    )}
                  </div>
                );
              })
            )}
            {/* Add Step Button */}
            <button
              type="button"
              className="mt-4 flex items-center justify-center gap-2 w-full py-3 px-6 bg-white border border-blue-500 rounded text-blue-600 text-sm font-medium hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={localSteps.some(s => (s as any).loading)}
              onClick={handleAddStep}
            >
              <Plus className="h-4 w-4" /> Add an Email
            </button>
          </div>
        </div>
      </div>

      {/* ── Step Editor Modal (Full-screen, portaled to body to escape stacking context) ── */}
      {showStepEditor && createPortal(
        <div className="fixed inset-0 bg-slate-100 z-[9999] flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 bg-slate-900 text-white">
            <div className="flex items-center gap-3">
              <span className="text-lg font-semibold">Edit Email Content</span>
              <span className="bg-blue-600 text-white text-xs font-medium px-2.5 py-1 rounded">Email {editingStepIndex + 1}</span>
            </div>
            <div className="flex items-center gap-3">
              {stepEditorUnsaved && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-900/30 border border-amber-500 rounded text-amber-300 text-xs font-medium">
                  <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />Unsaved
                </div>
              )}
              <Button variant="ghost" size="sm" className="text-white border-slate-600" onClick={handleCloseStepEditor}>
                <X className="h-4 w-4 mr-1" /> Done
              </Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleSaveStepContent}>
                <Save className="h-4 w-4 mr-1" /> Save Content
              </Button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <Suspense fallback={<div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>}>
              <EmailEditorHost
                key={`step-${localSteps[editingStepIndex]?.stepId ?? editingStepIndex}-${editorSeed}`}
                ref={editorRef}
                editorType={editorType}
                accessLevel={editorAccessLevel}
                initialHtml={editorInit.html}
                initialPlainContent={editorInit.plain}
                initialJson={editorInit.json}
                onSave={(html, plain, json) => {
                  if (editingStepIndex >= 0) {
                    const step = localSteps[editingStepIndex];
                    if (step) {
                      setLocalSteps(prev => prev.map((s, i) => i === editingStepIndex ? { ...s, htmlContent: html, plainContent: plain, jsonContent: json } : s));
                      updateStepMut.mutate({ stepId: step.stepId, data: { htmlContent: html, plainContent: plain, jsonContent: json } });
                    }
                  }
                  setStepEditorUnsaved(false);
                  setShowStepEditor(false);
                  toast.success('Email content saved');
                }}
                onChange={() => { if (!suppressUnsaved) setStepEditorUnsaved(true); }}
                onTemplateApply={(tmpl: FtpTemplateRow) => {
                  if (editingStepIndex < 0) return;
                  const step = localSteps[editingStepIndex];
                  if (!step) return;
                  const updData: Partial<AutomationStep> = {};
                  if (tmpl.subject && !step.subject) updData.subject = tmpl.subject;
                  if (tmpl.preheader && !step.preheader) updData.preheader = tmpl.preheader;
                  if (tmpl.from_email && !step.senderEmail) {
                    updData.senderEmail = tmpl.from_email;
                    updData.senderName = tmpl.from_name || '';
                  }
                  if (Object.keys(updData).length > 0) {
                    setLocalSteps(prev => prev.map((s, i) => i === editingStepIndex ? { ...s, ...updData } : s));
                    updateStepMut.mutate({ stepId: step.stepId, data: updData });
                  }
                }}
              />
            </Suspense>
          </div>
        </div>, document.body
      )}

      {/* ── Template Choice Modal (Start from Scratch or Choose Template) ── */}
      <Modal isOpen={showTemplateChoice} onClose={() => setShowTemplateChoice(false)} title="Create Email Content" size="sm">
        <div className="flex flex-col items-center gap-6 py-6">
          <Mail className="h-12 w-12 text-slate-300" />
          <p className="text-sm text-slate-600 text-center">How would you like to create the email content for this step?</p>
          <div className="flex items-center gap-3">
            {editorAccessLevel === 'full' && (
              <Button variant="secondary" onClick={handleTemplateStartFromScratch}>
                <FileText className="h-4 w-4 mr-2" />Start from Scratch
              </Button>
            )}
            <Button onClick={handleChooseTemplate}>
              <Mail className="h-4 w-4 mr-2" />Choose Template
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Editor Choice (Drag & Drop vs HTML) for start-from-scratch ── */}
      <EmailEditorChoiceModal
        isOpen={showEditorChoice}
        onClose={() => setShowEditorChoice(false)}
        onChoose={handleEditorChoice}
      />

      {/* ── Standard Template Picker ── */}
      <FtpTemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onApply={handleTemplateApply}
      />

      {/* ── Step Settings Modal ── */}
      <Modal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} title={`Email ${settingsStepIndex + 1} Settings`} size="md">
        <div className="space-y-4">
          <FormField label="Send Timing">
            <div className="inline-flex items-center gap-1.5">
              <Input type="number" min={0} value={settingsDelayValue} onChange={e => setSettingsDelayValue(Number(e.target.value))} className="w-16" />
              <Select value={settingsDelayUnit} onChange={v => setSettingsDelayUnit(v || 'days')} options={DELAY_UNIT_OPTIONS} className="w-24" />
              <span className="text-sm text-slate-500 whitespace-nowrap">after previous step</span>
            </div>
          </FormField>
          <FormField label="Subject Line" required>
            <Input value={settingsSubject} onChange={e => setSettingsSubject(e.target.value)} placeholder="Enter email subject" />
          </FormField>
          <FormField label="Preheader" helpText="Recommended: 40-100 characters">
            <Input value={settingsPreheader} onChange={e => setSettingsPreheader(e.target.value)} placeholder="Preview text shown after the subject" maxLength={100} />
          </FormField>
          <FormField label="From Sender Email" required>
            {senders.length === 0 ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                {/* The link is dropped without Marketing Settings access — the
                    message must not hint at a surface the user cannot reach. */}
                {hasPermission(PERMISSION.MARKETING_SETTINGS)
                  ? <>No verified sender emails found. <a href="/marketing/settings" className="underline">Add a sender</a>.</>
                  : 'No verified sender emails found.'}
              </div>
            ) : (
              <Select
                value={settingsSender}
                onChange={v => {
                  setSettingsSender(v || '');
                  const s = senders.find(x => x.email === v);
                  setSettingsSenderName(s?.senderName || '');
                }}
                options={[
                  { value: '', label: '-- Select sender email --' },
                  ...senders.map(s => ({ value: s.email, label: `${s.email} (${s.senderName})` })),
                ]}
              />
            )}
          </FormField>
          <FormField label="From Name">
            <Input value={settingsSenderName} disabled className="bg-slate-50" placeholder="Selected automatically from sender email" />
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* ── Timing Modal ── */}
      <Modal isOpen={showTimingModal} onClose={() => setShowTimingModal(false)} title="Edit Timing" size="md">
        <div className="space-y-4">
          <FormField label="Wait time before sending" helpText="Set to 0 to send immediately">
            <div className="inline-flex items-center gap-1.5">
              <Input type="number" min={0} value={timingValue} onChange={e => setTimingValue(Number(e.target.value))} className="w-16" />
              <Select value={timingUnit} onChange={v => setTimingUnit(v || 'days')} options={DELAY_UNIT_OPTIONS} className="w-24" />
              <span className="text-sm text-slate-500 whitespace-nowrap">after previous step</span>
            </div>
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowTimingModal(false)}>Cancel</Button>
            <Button onClick={handleSaveTiming}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Automation"
        message="Are you sure you want to delete this automation? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deleteMut.mutate()}
        onClose={() => setShowDeleteConfirm(false)}
      />

      {/* Pause Confirm */}
      <PauseAutomationDialog
        isOpen={pauseConfirmOpen}
        channel="email"
        automationName={automationName || automation?.name}
        activeContacts={contacts.active}
        onClose={() => setPauseConfirmOpen(false)}
        onConfirm={confirmPause}
      />
    </div>
  );
}

